import type {
  FeasibilityStatus,
  ResidualValueResult,
  SaleStrategy,
} from '@popety-io/types'

export const DEFAULT_TARGET_DEVELOPER_PROFIT_PCT = 15
export const DEVELOPER_MARGIN_MARGINAL_GAP_PCT = 5
export const AT_BID_EPSILON_CHF = 1

export interface ResidualValueInput {
  /** GDV / STWE sales proceeds (or cap-derived sale price for block_sale). */
  project_value: number
  /** BKP 1–9 construction costs. Land (BKP 0) is excluded. */
  construction_costs: number
  financing_costs: number
  /** Target developer profit as % of GDV. */
  target_profit_pct: number
  plot_area_m2: number
  /** BKP 0 land cost — comparison only, never an input to residual. */
  entered_land_cost?: number
}

/**
 * Residual land value (Maximum Bid).
 *
 * residual = project_value − (construction + financing) − (project_value × target%)
 *
 * Land acquisition is the unknown, so BKP 0 is not subtracted. Entered land
 * cost is compared afterwards as headroom vs the max bid.
 */
export function computeResidualValue(
  input: ResidualValueInput
): ResidualValueResult {
  const project_costs_ex_land = input.construction_costs + input.financing_costs
  const target_profit = (input.project_value * input.target_profit_pct) / 100
  const residual_value =
    input.project_value - project_costs_ex_land - target_profit
  const plot_area_m2 = input.plot_area_m2
  const residual_per_m2 =
    plot_area_m2 > 0 ? residual_value / plot_area_m2 : null

  const result: ResidualValueResult = {
    project_value: input.project_value,
    project_costs_ex_land,
    target_profit_pct: input.target_profit_pct,
    target_profit,
    residual_value,
    residual_per_m2,
    plot_area_m2,
  }

  const entered = input.entered_land_cost
  if (entered != null && entered > 0) {
    const headroom = residual_value - entered
    result.entered_land_cost = entered
    result.headroom = headroom
    if (Math.abs(headroom) < AT_BID_EPSILON_CHF) {
      result.bid_status = 'at'
    } else if (headroom > 0) {
      result.bid_status = 'below'
    } else {
      result.bid_status = 'above'
    }
  }

  return result
}

/** Hold-strategy residual is an IRR/NPV solve, not STWE residual. */
export function residualForSaleStrategy(
  strategy: SaleStrategy | undefined,
  input: ResidualValueInput
): ResidualValueResult | undefined {
  if (strategy === 'hold') return undefined
  return computeResidualValue(input)
}

/**
 * Cost-based developer-margin viability. Target is the same numeric hurdle
 * as residual (default 15) but applied to net_profit / total_cost, not GDV.
 */
export function developerMarginStatus(
  marginPct: number,
  targetPct: number
): FeasibilityStatus {
  if (marginPct >= targetPct) return 'viable'
  if (marginPct >= targetPct - DEVELOPER_MARGIN_MARGINAL_GAP_PCT) {
    return 'marginal'
  }
  return 'not_viable'
}

export function resolvePlotAreaM2(
  land?: { area?: number | null; official_area?: number | null } | null
): number {
  return land?.area || land?.official_area || 0
}
